import { action, computed, observable, reaction, makeObservable } from 'mobx';
import { ContentLookup, TimeZone, User, X4Unit, X5Lesson } from '../../../../domain';
import {
    Control,
    currentTimeZone,
    dateFormat,
    Disposers,
    logger,
    makeTime,
    newValueForSelection,
    StringControl,
    validators
} from '../../../../lib/helpers';
import { ClassroomStatus, ClassroomUpdateModel } from '../../../classroom/models/classroom-models';

function setDateTime(time: number, dateControl: StringControl, timeControl: StringControl, timeZone: TimeZone) {
    dateControl.setValue(dateFormat(time, 'ymd', timeZone));
    timeControl.setValue(dateFormat(time, 'hm', timeZone));
}

function clearDateTime(dateControl: StringControl, timeControl: StringControl) {
    dateControl.setValue('');
    timeControl.setValue('');
}

type ControlNames = 'admin' | 'student' | 'teacher' | 'utc';

export class ClassroomEditFormStore {
    readonly student: Control<User | null>;
    readonly teacher: Control<User | null>;

    // we want to prevent infinite updates
    readonly skipUpdate: Set<ControlNames>;

    // seconds since epoch start
    readonly startTime: Control<number | null>;
    readonly adminStartDate: StringControl;
    readonly adminStartTime: StringControl;
    readonly studentStartDate: StringControl;
    readonly studentStartTime: StringControl;
    readonly teacherStartDate: StringControl;
    readonly teacherStartTime: StringControl;
    readonly PendingTime: StringControl;

    @observable.ref
    unitOptions: readonly X4Unit[];

    readonly unit: Control<X4Unit | null>;

    @observable.ref
    lessonOptions: readonly X5Lesson[];

    readonly lesson: Control<X5Lesson | null>;

    readonly status: Control<ClassroomStatus | null>;

    private readonly disposers = new Disposers();

    constructor(private readonly contentLookup: ContentLookup) {
        makeObservable(this);
        this.student = new Control<User | null>('', null);
        this.teacher = new Control<User | null>('', null);

        this.startTime = new Control<number | null>('', null);
        this.skipUpdate = new Set<ControlNames>();
        this.adminStartDate = new StringControl(
            '', '', [validators.strictDate]);
        this.adminStartTime = new StringControl(
            '', '', [validators.time]);
        this.studentStartDate = new StringControl(
            '', '', [validators.strictDate]);
        this.studentStartTime = new StringControl(
            '', '', [validators.time]);
        this.teacherStartDate = new StringControl(
            '', '', [validators.strictDate]);
        this.teacherStartTime = new StringControl(
            '', '', [validators.time]);
        this.PendingTime = new StringControl('', '', [validators.integer]);

        this.unit = new Control<X4Unit | null>(
            '', null, validators.required);
        this.lesson = new Control<X5Lesson | null>(
            '', null, validators.required);
        this.status = new Control<ClassroomStatus | null>(
            '', 'Scheduled', validators.required);
        this.unitOptions = this.lessonOptions = [];
        this.setup();
        // this.dump();
    }

    /****************************************************************************/

    private beginDateTimeUpdate(skip: ControlNames) {
        this.skipUpdate.clear();
        this.skipUpdate.add(skip);
    }

    @action
    setStartTime(value: number) {
        this.beginDateTimeUpdate('utc');
        this.startTime.setValue(Math.floor(value));
    }

    @action
    nextWeek = () => {
        const startTime = this.startTime.value;
        if (startTime === null)
            return;
        this.setStartTime(startTime + 7 * 24 * 60 * 60);
    };

    @action
    previousWeek = () => {
        const startTime = this.startTime.value;
        if (startTime === null)
            return;
        this.setStartTime(startTime - 7 * 24 * 60 * 60);
    };

    @action
    adminStartDateChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        this.beginDateTimeUpdate('admin');
        this.adminStartDate.onChangeHandler(event);
    };

    @action
    adminStartTimeChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        this.beginDateTimeUpdate('admin');
        this.adminStartTime.onChangeHandler(event);
    };

    @action
    studentStartDateChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        this.beginDateTimeUpdate('student');
        this.studentStartDate.onChangeHandler(event);
    };

    @action
    studentStartTimeChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        this.beginDateTimeUpdate('student');
        this.studentStartTime.onChangeHandler(event);
    };

    @action
    teacherStartDateChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        this.beginDateTimeUpdate('teacher');
        this.teacherStartDate.onChangeHandler(event);
    };

    @action
    teacherStartTimeChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        this.beginDateTimeUpdate('teacher');
        this.teacherStartTime.onChangeHandler(event);
    };

    /****************************************************************************/

    @action
    pendingTimeChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        this.PendingTime.onChangeHandler(event);
    };

    /****************************************************************************/

    @action
    setStudent = (student: User | null) => {
        this.beginDateTimeUpdate('utc');
        this.student.setValue(student);
    };

    @action
    setTeacher = (teacher: User | null) => {
        this.beginDateTimeUpdate('utc');
        this.teacher.setValue(teacher);
    };

    @action
    setLesson(lesson: X5Lesson | null) {
        if (this.student === null || lesson === null)
            return;
        this.updateUnitOptions();
        this.unit.setValue(newValueForSelection(this.unitOptions, lesson.Unit));
        this.updateLessonOptions();
        this.lesson.setValue(newValueForSelection(this.lessonOptions, lesson));
    }

    @computed
    get valid(): boolean {
        return this.teacher.value !== null &&
            this.student.value !== null &&
            this.startTime.value !== null &&
            this.lesson.value !== null &&
            this.status.value !== null &&
            this.PendingTime.value !== null;
    }

    @computed
    get model(): ClassroomUpdateModel {
        if (!this.valid)
            throw new Error('Invalid form');
        return {
            StudentId: this.student.value!.Id,
            TeacherId: this.teacher.value!.Id,
            LessonId: this.lesson.value!.Id,
            StartTime: this.startTime.value!,
            Status: this.status.value!,
            PendingTime: Number.parseInt(this.PendingTime.value)
        };
    }

    @action
    markAllTouched() {
        [
            this.student, this.teacher,
            this.adminStartDate, this.adminStartTime,
            this.unit, this.lesson, this.status, this.PendingTime
        ].forEach(x => x.markTouched());
    }

    private setup() {
        const fireImmediately = { fireImmediately: true } as const;
        // update select options
        this.disposers.add(
            reaction(
                () => this.student.value,
                this.updateUnitOptions,
                fireImmediately),
            reaction(
                () => this.unit.value,
                this.updateLessonOptions,
                fireImmediately));

        // keep teacher, admin, student start time in sync
        this.disposers.add(
            reaction(
                () => [this.student.value,
                this.studentStartDate.value,
                this.studentStartTime.value],
                this.updateStartTimeFromStudent,
                fireImmediately),
            reaction(
                () => [this.teacher.value,
                this.teacherStartDate.value,
                this.teacherStartTime.value],
                this.updateStartTimeFromTeacher,
                fireImmediately),
            reaction(
                () => [
                    this.adminStartDate.value,
                    this.adminStartTime.value],
                this.updateStartTimeFromAdmin,
                fireImmediately));

        this.disposers.add(
            reaction(
                () => [this.startTime.value, this.student.value],
                this.updateStudentStartDateTime,
                fireImmediately),
            reaction(
                () => [this.startTime.value, this.teacher.value],
                this.updateTeacherStartDateTime,
                fireImmediately),
            reaction(
                () => [this.startTime.value],
                this.updateAdminStartDateTime,
                fireImmediately));
    }

    @action
    private updateStartTimeFromStudent = () => {
        if (this.skipUpdate.has('utc'))
            return;
        this.skipUpdate.add('utc');
        if (this.student.value === null)
            return;

        const time = makeTime(this.studentStartDate.value,
            this.studentStartTime.value,
            this.student.value.TimeZone!);
        this.startTime.setValue(time);
    };

    @action
    private updateStartTimeFromTeacher = () => {
        if (this.skipUpdate.has('utc'))
            return;
        this.skipUpdate.add('utc');
        if (this.teacher.value === null)
            return;

        const time = makeTime(this.teacherStartDate.value,
            this.teacherStartTime.value,
            this.teacher.value.TimeZone!);
        this.startTime.setValue(time);
    };

    @action
    private updateStartTimeFromAdmin = () => {
        if (this.skipUpdate.has('utc'))
            return;
        this.skipUpdate.add('utc');

        const time = makeTime(this.adminStartDate.value,
            this.adminStartTime.value,
            currentTimeZone());
        this.startTime.setValue(time);
    };

    @action
    private updateStudentStartDateTime = () => {
        const time = this.startTime.value;
        const student = this.student.value;
        const skip = this.skipUpdate.has('student');
        this.skipUpdate.add('student');

        if (student === null) {
            clearDateTime(this.studentStartDate, this.studentStartTime);
            return;
        }

        if (skip)
            return;
        if (time === null)
            clearDateTime(this.studentStartDate, this.studentStartTime);
        else
            setDateTime(time, this.studentStartDate, this.studentStartTime, student.TimeZone!);
    };

    @action
    private updateTeacherStartDateTime = () => {
        const time = this.startTime.value;
        const teacher = this.teacher.value;
        const skip = this.skipUpdate.has('teacher');
        this.skipUpdate.add('teacher');

        if (teacher === null) {
            clearDateTime(this.teacherStartDate, this.teacherStartTime);
            return;
        }

        if (skip)
            return;
        if (time === null)
            clearDateTime(this.teacherStartDate, this.teacherStartTime);
        else
            setDateTime(time, this.teacherStartDate, this.teacherStartTime, teacher.TimeZone!);
    };

    @action
    private updateAdminStartDateTime = () => {
        const time = this.startTime.value;
        const skip = this.skipUpdate.has('admin');
        this.skipUpdate.add('admin');

        if (skip)
            return;
        if (time === null)
            clearDateTime(this.adminStartDate, this.adminStartTime);
        else
            setDateTime(time, this.adminStartDate, this.adminStartTime, currentTimeZone());
    };

    @action
    private updateUnitOptions = () => {
        const levelId = this.student?.value?.Student?.LevelId;
        this.unitOptions = [];
        if (levelId !== null && levelId !== undefined) {
            const level = this.contentLookup.X3LevelById[levelId];
            if (level !== undefined)
                this.unitOptions = level.Units!;
        }
        this.unit.setValue(newValueForSelection(this.unitOptions, this.unit.value));
    };

    @action
    private updateLessonOptions = () => {
        const unit = this.unit.value;
        this.lessonOptions = [];
        if (unit != null)
            this.lessonOptions = unit.Lessons!;
        this.lesson.setValue(newValueForSelection(this.lessonOptions, this.lesson.value));
    };

    private dump() {
        reaction(
            () => this.startTime.value,
            () => logger.log(
                'startTime',
                this.startTime.value,
                dateFormat(this.startTime.value, 'ymdhms', true)),
            { fireImmediately: true });
        reaction(
            () => [
                this.student.value,
                this.teacher.value,
                this.startTime.value,
                this.adminStartDate.value,
                this.adminStartTime.value,
                this.studentStartDate.value,
                this.studentStartTime.value,
                this.teacherStartDate.value,
                this.teacherStartTime.value
            ],
            () =>
                logger.log(JSON.stringify({
                    student: this.student.value,
                    teacher: this.teacher.value,
                    startTime: this.startTime.value,
                    startTimeStr: dateFormat(this.startTime.value, 'ymdhms', true),
                    adminStartDate: this.adminStartDate.value,
                    adminStartTime: this.adminStartTime.value,
                    studentStartDate: this.studentStartDate.value,
                    studentStartTime: this.studentStartTime.value,
                    teacherStartDate: this.teacherStartDate.value,
                    teacherStartTime: this.teacherStartTime.value
                }))
        );
    }

    dispose = () => {
        this.disposers.dispose();
    };

}
